import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress, 
  Snackbar,
  Alert,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { database } from '../../firebase';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { ref, push, set, get } from 'firebase/database';
import firebaseDataService from '../../services/FirebaseDataService';
import { useTranslation } from 'react-i18next';

interface CalendarEventData {
  id?: string;
  title: string;
  description: string;
  color?: string; // Etkinlik için renk kodu
  createdAt?: number;
}

interface CalendarEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (eventData: CalendarEventData) => void;
}

// Varsayılan renk seçenekleri
const COLOR_OPTIONS = [
  { value: '#1976d2', name: 'blueDefault' },
  { value: '#2e7d32', name: 'green' },
  { value: '#d32f2f', name: 'red' },
  { value: '#ed6c02', name: 'orange' },
  { value: '#9c27b0', name: 'purple' },
  { value: '#00796b', name: 'turquoise' },
  { value: '#ff5722', name: 'orangeRed' },
  { value: '#795548', name: 'brown' },
  { value: '#607d8b', name: 'grayBlue' },
  { value: '#f44336', name: 'lightRed' }
];

const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  open,
  onClose,
  onSave
}) => {
  const { t } = useTranslation('other');
  const [eventData, setEventData] = useState<CalendarEventData>({
    title: '',
    description: '',
    color: '#1976d2' // Varsayılan mavi renk
  });

  const [errors, setErrors] = useState({
    title: false
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Kullanıcı ve şirket bilgilerini yükleme
  useEffect(() => {
    const loadCompanyId = async () => {
      try {
        // Giriş yapmış kullanıcıyı kontrol et
        const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
        if (!user) return;
        
        // Kullanıcı verilerini al ve şirket ID'sini bul
        const userData = await firebaseDataService.getUserData(user.uid);
        if (!userData) return;
        
        const userCompanyId = userData.companyId || null;
        setCompanyId(userCompanyId);
      } catch (error) {
        console.error('Error while loading company ID:', error);
      }
    };
    
    if (open) {
      loadCompanyId();
    }
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEventData({
      ...eventData,
      [name]: value
    });

    if (name === 'title') {
      setErrors({
        ...errors,
        title: value.trim() === ''
      });
    }
  };

  // Renk seçimi değişikliği
  const handleColorChange = (
    event: React.MouseEvent<HTMLElement>,
    newColor: string | null
  ) => {
    if (newColor) {
      setEventData({
        ...eventData,
        color: newColor
      });
    }
  };

  const handleSubmit = async () => {
    // Form doğrulama
    if (eventData.title.trim() === '') {
      setErrors({
        ...errors,
        title: true
      });
      return;
    }

    if (!companyId) {
      setSnackbarMessage(t('events.calendarEventModal.errors.companyInfoNotFound'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsSaving(true);

    try {
      // Yeni bir özel etkinlik oluştur
      const newEvent = {
        ...eventData,
        createdAt: Date.now(),
        isChecked: true // Varsayılan olarak seçili
      };
      
      // Firebase veritabanına kaydet
      const eventsRef = ref(database, `companies/${companyId}/events/calendarEvents`);
      const newEventRef = push(eventsRef); // Yeni bir ID oluştur
      
      // ID ile birlikte kaydet
      await set(newEventRef, {
        ...newEvent,
        id: newEventRef.key || undefined
      });
      
      // Başarılı mesajı göster
      setSnackbarMessage(t('events.calendarEventModal.messages.saved'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // onSave fonksiyonunu çağır
      onSave({
        ...newEvent,
        id: newEventRef.key || undefined
      });
      
      // Modalı kapat ve formu sıfırla
      setEventData({
        title: '',
        description: '',
        color: '#1976d2' // Varsayılan renk
      });
      onClose();
    } catch (error) {
      console.error('Error while saving special event:', error);
      setSnackbarMessage(t('events.calendarEventModal.errors.saveFailed'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    // Modalı kapatırken formu sıfırla
    setEventData({
      title: '',
      description: '',
      color: '#1976d2' // Varsayılan renk
    });
    setErrors({
      title: false
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ mr: 1 }} />
          <Typography variant="h6">
            {t('events.calendarEventModal.title')}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: 'white'
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 3 }}>
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          bgcolor: 'info.light', 
          borderRadius: 1, 
          color: 'info.contrastText' 
        }}>
          <Typography variant="body2">
            {t('events.calendarEventModal.info')}
          </Typography>
        </Box>
        
        <TextField
          name="title"
          label={t('events.calendarEventModal.fields.eventName')}
          fullWidth
          value={eventData.title}
          onChange={handleInputChange}
          error={errors.title}
          helperText={errors.title ? t('events.calendarEventModal.validation.eventNameRequired') : ''}
          autoFocus
          required
          variant="outlined"
          sx={{ mb: 3 }}
        />
        
        <TextField
          name="description"
          label={t('events.calendarEventModal.fields.description')}
          fullWidth
          multiline
          rows={4}
          value={eventData.description}
          onChange={handleInputChange}
          variant="outlined"
          sx={{ mb: 3 }}
        />

        {/* Renk Seçici */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ 
              mb: 1, 
              display: 'flex', 
              alignItems: 'center',
              color: 'text.secondary'
            }}
          >
            <ColorLensIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('events.calendarEventModal.fields.eventColor')}
          </Typography>
          
          <Paper 
            variant="outlined"
            sx={{ 
              p: 2, 
              borderRadius: 1,
              borderColor: 'divider' 
            }}
          >
            {/* Renk önizleme */}
            <Box
              sx={{
                width: '100%',
                height: '24px',
                bgcolor: eventData.color || '#1976d2',
                borderRadius: 1,
                mb: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
            
            <ToggleButtonGroup
              value={eventData.color}
              exclusive
              onChange={handleColorChange}
              aria-label={t('events.calendarEventModal.fields.eventColor')}
              size="small"
              sx={{
                flexWrap: 'wrap',
                justifyContent: 'center',
                '& .MuiToggleButtonGroup-grouped': {
                  m: 0.5,
                  borderRadius: '50% !important',
                  border: 'none !important',
                  width: 36,
                  height: 36,
                  padding: 0,
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&.Mui-selected': {
                    border: '3px solid rgba(0,0,0,0.2) !important'
                  }
                }
              }}
            >
              {COLOR_OPTIONS.map((color) => (
                <ToggleButton 
                  key={color.value} 
                  value={color.value}
                  sx={{ 
                    bgcolor: `${color.value} !important`,
                    '&:hover': { 
                      bgcolor: `${color.value} !important`,
                      opacity: 0.8 
                    }
                  }}
                >
                  <Tooltip title={t(`events.calendarEventModal.colors.${color.name}`)}>
                    <Box />
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            
            <Typography 
              variant="caption" 
              sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}
            >
              {t('events.calendarEventModal.fields.eventColorHelper')}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ mr: 1 }} disabled={isSaving}>
          {t('events.calendarEventModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          startIcon={isSaving ? <CircularProgress size={24} color="inherit" /> : <EventIcon />}
          disabled={isSaving}
        >
          {isSaving ? t('events.calendarEventModal.actions.saving') : t('events.calendarEventModal.actions.save')}
        </Button>
      </DialogActions>

      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CalendarEventModal; 